import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Flame, Gift, Heart, Sparkles, Clock } from 'lucide-react';
import { Button } from "@/components/ui/button";
import ProductCard from '../components/ProductCard';
import { API } from '../App';

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Fetch featured products
        const featuredRes = await fetch(`${API}/products?featured=true`);
        if (featuredRes.ok) {
          const data = await featuredRes.json();
          setFeaturedProducts(data.slice(0, 4));
        }

        // Fetch all products for new arrivals (sorted by newest)
        const allRes = await fetch(`${API}/products`);
        if (allRes.ok) {
          const data = await allRes.json();
          // Sort by created_at descending and take first 4
          const sorted = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          setNewArrivals(sorted.slice(0, 4));
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
      }
      setLoading(false);
    };
    fetchProducts();
  }, []);

  const categories = [
    { 
      id: 'candles', 
      name: 'Candles', 
      image: 'https://images.unsplash.com/photo-1550880916-205ed93d8573?crop=entropy&cs=srgb&fm=jpg&q=85',
      span: 'col-span-2 row-span-2'
    },
    { 
      id: 'wax-melts', 
      name: 'Wax Melts', 
      image: 'https://images.unsplash.com/photo-1758545692685-e61b35b7e0bb?crop=entropy&cs=srgb&fm=jpg&q=85',
      span: 'col-span-1 row-span-1'
    },
    { 
      id: 'snap-bars', 
      name: 'Snap Bars', 
      image: 'https://images.unsplash.com/photo-1607713109008-d00372938c2d?crop=entropy&cs=srgb&fm=jpg&q=85',
      span: 'col-span-1 row-span-1'
    },
    { 
      id: 'bundles', 
      name: 'Gift Bundles', 
      image: 'https://images.unsplash.com/photo-1641544014231-2e0080672af7?crop=entropy&cs=srgb&fm=jpg&q=85',
      span: 'col-span-2 row-span-1'
    },
  ];

  const features = [
    { icon: Flame, title: 'Hand-Poured', description: 'Each candle crafted with care' },
    { icon: Heart, title: 'Eco-Friendly', description: 'Sustainable soy wax' },
    { icon: Gift, title: 'Perfect Gifts', description: 'Beautiful packaging included' },
    { icon: Sparkles, title: 'Premium Scents', description: 'Unique fragrance blends' },
  ];

  return (
    <div className="min-h-screen" data-testid="homepage">
      {/* Hero Section - Warm Candle Theme */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image - Cozy Candle */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1670346304550-01c91fbc15af?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Warm glowing candles"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-cursive text-2xl md:text-3xl text-[#E5C88A] mb-4"
          >
            Handcrafted in Scotland
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white mb-6 leading-tight"
          >
            Glow & Gather
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto"
          >
            Fill your home with warmth and tranquility. Our handcrafted luxury candles bring cosy moments to life with premium scents and a beautiful glow.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/shop">
              <Button 
                className="btn-primary text-lg px-10 py-6 gold-glow"
                data-testid="hero-shop-btn"
              >
                Shop Collection
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#2C2C2C] rounded-full px-10 py-6 text-lg">
                Our Story
              </Button>
            </Link>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-1.5 bg-white rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Features Bar */}
      <section className="bg-[#5D6D53] py-8" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4 justify-center"
              >
                <feature.icon className="w-6 h-6 text-[#E5C88A]" />
                <div>
                  <p className="text-white font-medium">{feature.title}</p>
                  <p className="text-white/70 text-sm">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* New Arrivals Section */}
      <section className="py-24 px-6 md:px-12 bg-white" data-testid="new-arrivals-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4"
          >
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-[#C5A059]" />
                <p className="font-cursive text-2xl text-[#C5A059]">Just Landed</p>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C]">New Arrivals</h2>
              <p className="text-[#595959] mt-2 max-w-md">
                Discover our latest creations, freshly poured and ready to bring warmth to your home.
              </p>
            </div>
            <Link to="/shop">
              <Button variant="ghost" className="text-[#C5A059] hover:text-[#B08D45] font-medium">
                View All New
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-[#F5F1EB] rounded-lg animate-pulse">
                  <div className="aspect-[3/4] bg-stone-200 rounded-t-lg" />
                  <div className="p-4 space-y-3">
                    <div className="h-3 bg-stone-200 rounded w-1/3" />
                    <div className="h-5 bg-stone-200 rounded w-2/3" />
                    <div className="h-4 bg-stone-200 rounded w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : newArrivals.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {newArrivals.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative"
                >
                  {/* New Badge */}
                  <div className="absolute top-3 right-3 z-10 bg-[#D48C70] text-white text-xs px-3 py-1 rounded-full font-medium">
                    New
                  </div>
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#F5F1EB] rounded-xl">
              <p className="text-[#8C8C8C]">New products coming soon!</p>
              <Link to="/shop" className="text-[#C5A059] hover:underline mt-2 inline-block">
                Browse current collection →
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Categories Bento Grid */}
      <section className="py-24 px-6 md:px-12 bg-[#FAF9F6]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-cursive text-2xl text-[#C5A059] mb-2">Explore</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C]">Our Collections</h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 auto-rows-[200px] md:auto-rows-[250px]">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`${category.span} relative overflow-hidden rounded-xl group cursor-pointer`}
              >
                <Link to={`/shop/${category.id}`} data-testid={`category-${category.id}`}>
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-serif text-2xl md:text-3xl text-white group-hover:text-[#E5C88A] transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-white/70 text-sm mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      Shop now →
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 px-6 md:px-12 bg-[#F5F1EB] grain-texture" data-testid="featured-section">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4"
          >
            <div>
              <p className="font-cursive text-2xl text-[#C5A059] mb-2">Customer Favourites</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C]">Bestsellers</h2>
            </div>
            <Link to="/shop">
              <Button variant="ghost" className="text-[#C5A059] hover:text-[#B08D45] font-medium">
                View All Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg animate-pulse">
                  <div className="aspect-[3/4] bg-stone-200 rounded-t-lg" />
                  <div className="p-4 space-y-3">
                    <div className="h-3 bg-stone-200 rounded w-1/3" />
                    <div className="h-5 bg-stone-200 rounded w-2/3" />
                    <div className="h-4 bg-stone-200 rounded w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {featuredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-[#8C8C8C]">Check back soon for featured products!</p>
              <Link to="/shop" className="text-[#C5A059] hover:underline mt-2 inline-block">
                Browse all products →
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Cozy Atmosphere Banner */}
      <section className="relative py-32 px-6 md:px-12 overflow-hidden" data-testid="atmosphere-banner">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1758545692685-e61b35b7e0bb?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Cozy candle atmosphere"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-cursive text-2xl text-[#E5C88A] mb-4">Create Your Sanctuary</p>
            <h2 className="font-serif text-3xl md:text-5xl text-white mb-6 leading-tight">
              Transform Any Space Into a Cosy Retreat
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              There's something magical about the soft glow of candlelight. Let our handcrafted candles set the mood for your perfect evening.
            </p>
            <Link to="/shop">
              <Button className="btn-primary text-lg px-10 py-6 gold-glow">
                Shop Now
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 px-6 md:px-12 bg-[#2C2C2C]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-cursive text-2xl text-[#E5C88A] mb-4">Stay Connected</p>
            <h2 className="font-serif text-4xl md:text-5xl text-white mb-6">Join Our Community</h2>
            <p className="text-stone-400 mb-8">
              Be the first to know about new scents, special offers, and cosy inspiration delivered straight to your inbox.
            </p>
            <Link to="/contact">
              <Button className="btn-primary text-lg px-10 py-6 gold-glow">
                Get in Touch
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
